/**
 * Teri's conversation state, held at APP level (product rule 2026-07-21):
 * the chat is a fixed companion window — it must survive navigation anywhere
 * on the platform (management, learner surface, course player) until the
 * member closes it. The launcher/panel components render per layout, but the
 * open flag and the message history live here, above the router's remounts.
 *
 * Since the v2 wire contract the SERVER owns the transcript: we post one
 * message plus a conversation id, and both turns are persisted. This context
 * therefore holds a *view* of a server-side conversation — optimistic bubbles
 * that get stamped with real ULIDs when the `meta` frame lands.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  AssistantRequestError,
  streamAssistantChat,
} from '../services/assistant';
import {
  assistantKeys,
  deleteAssistantAttachment,
  getConversation,
  uploadAssistantAttachment,
} from '../services/assistantConversations';
import type {
  AssistantAttachment,
  AssistantMention,
  AssistantMessage,
  AssistantMessageStatus,
  AssistantPage,
  ChatMeta,
  DroppedMention,
} from '../types/assistant';

/** One bubble on screen. Wire data plus the client-only bits around it. */
export interface TeriMessage {
  /** Server ULID once known, a `local:` placeholder until the meta frame. */
  id: string;
  from: 'teri' | 'me';
  text: string;
  /** Display time, already formatted. */
  at: string;
  status: AssistantMessageStatus;
  attachments: AssistantAttachment[];
  mentions: AssistantMention[];
  /**
   * Non-blocking explanations rendered under the bubble — a mention the
   * member wasn't allowed to include, a document that didn't fit. The answer
   * still arrives; these never block or toast.
   */
  notes: string[];
  /** Images were attached but the provider couldn't see them. */
  visionBlind: boolean;
  /**
   * Local object URLs for images sent this session, keyed by attachment id.
   * The API returns attachment *metadata* only — no download URL — so after a
   * reload a sent image falls back to its filename chip. That is the honest
   * fallback rather than a broken <img>.
   */
  previewUrls: Record<string, string>;
}

/**
 * One reply being streamed. Runs are tracked per conversation and keep going
 * when the member switches chats: aborting the fetch makes the server persist
 * a TRUNCATED answer (it sees `GeneratorExit` and saves what it had), so
 * "leave and come back" would silently cost the member the rest of the reply.
 * Instead the stream is drained to the end and simply stops painting.
 */
interface ActiveRun {
  id: string;
  /** The chat it belongs to; null until the `meta` frame names it. */
  conversationId: string | null;
  /** Which unsent chat it started in, so a pre-`meta` switch is detectable. */
  draftKey: string;
  controller: AbortController;
  userBubbleId: string;
  assistantBubbleId: string;
  stamp: string;
  /** Everything received so far — repainted if the member comes back mid-answer. */
  streamed: string;
  title: string | null;
  notes: string[];
  visionBlind: boolean;
}

/** A reply that finished while the member was somewhere else. */
export interface AnsweredNotice {
  conversationId: string;
  title: string;
  status: AssistantMessageStatus;
}

/** A file being staged for the next message. */
export interface PendingAttachment {
  localKey: string;
  file: File;
  /** Object URL for an instant image preview; null for documents. */
  previewUrl: string | null;
  /** 0–100 while uploading. */
  progress: number;
  /** The server record, once the upload settles. */
  attachment?: AssistantAttachment;
  error?: string;
}

/** Where the chat sits: a corner window, or a rail that squeezes the page. */
export type AssistantDock = 'floating' | 'sidebar';

const DOCK_KEY = 'tereiko-teri-dock';
const CONVERSATION_KEY = 'tereiko-teri-conversation';

/** Server caps: 5 attachments and 2500 characters per turn. */
export const MAX_ATTACHMENTS_PER_TURN = 5;
export const MAX_MESSAGE_CHARS = 2500;

/** Rail width. Single source of truth: the panel renders at it and the CSS
 *  var below pads the body by it, so the two can never disagree. */
export const ASSISTANT_RAIL_WIDTH = 380;

interface AssistantContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  dock: AssistantDock;
  setDock: (dock: AssistantDock) => void;
  /** Null until the first turn of a new chat has been answered. */
  conversationId: string | null;
  /** Server-owned title of the current chat; null for an unsent new chat. */
  conversationTitle: string | null;
  messages: TeriMessage[];
  draft: string;
  setDraft: (draft: string) => void;
  /** True when the chat ON SCREEN is waiting for a reply. */
  thinking: boolean;
  /** Chats still being answered in the background, by conversation id. */
  answeringIds: string[];
  /** The most recent reply that finished off-screen, if it hasn't been seen. */
  answeredNotice: AnsweredNotice | null;
  openAnsweredNotice: () => void;
  dismissAnsweredNotice: () => void;
  send: (text: string) => Promise<void>;
  /** Abort the visible chat's reply, keeping whatever Teri already said. */
  stop: () => void;
  /** Start a fresh conversation (keeps the panel open). */
  newChat: () => void;
  /** Load a saved chat and resume it. */
  openConversation: (id: string) => Promise<void>;
  loadingConversation: boolean;
  /** Older turns exist above the loaded page. */
  hasMore: boolean;
  loadingOlder: boolean;
  loadOlder: () => Promise<void>;
  historyOpen: boolean;
  setHistoryOpen: (open: boolean) => void;
  staged: PendingAttachment[];
  attachFiles: (files: File[]) => Promise<void>;
  removeStaged: (localKey: string) => void;
  pendingMentions: AssistantMention[];
  addMention: (mention: AssistantMention) => void;
  removeMention: (id: string) => void;
}

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

const timeNow = () =>
  new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const formatTime = (iso: string) => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

let localSeq = 0;
const localId = (prefix: string) => `local:${prefix}:${++localSeq}`;
const isLocalId = (id: string) => id.startsWith('local:');

/** Human label for the page the member is on — sent with every message so
 *  Teri answers about THIS screen ("these numbers") without asking which. */
const PAGE_LABELS: [prefix: string, label: string][] = [
  ['/dashboard', 'Dashboard'],
  ['/courses', 'Course Library'],
  ['/programs', 'Learning Programs'],
  ['/analytics', 'Analytics'],
  ['/gamification', 'Gamification'],
  ['/prizes', 'Prize Management'],
  ['/learners', 'Learners'],
  ['/settings', 'Settings'],
  ['/chat', 'Chat'],
  ['/calendar', 'Calendar'],
  ['/learn/dashboard', 'Home (learner)'],
  ['/learn/courses', 'My Courses (learner)'],
  ['/learn/programs', 'Programs (learner)'],
  ['/learn/prizes', 'Points & Prizes (learner)'],
  ['/learn/certificates', 'Certificates (learner)'],
  ['/learn/chat', 'Chat (learner)'],
  ['/learn/calendar', 'Calendar (learner)'],
  ['/learn/', 'Learner surface'],
];

function pageFor(pathname: string): AssistantPage {
  // Longest-prefix match so /learn/prizes beats /learn/.
  const hit = [...PAGE_LABELS]
    .sort((a, b) => b[0].length - a[0].length)
    .find(([prefix]) => pathname.startsWith(prefix));
  return { path: pathname, label: hit ? hit[1] : 'Tereiko' };
}

/** A stored turn becomes a bubble. Order is the server's — never re-sort. */
function toTeriMessage(message: AssistantMessage): TeriMessage {
  return {
    id: message.id,
    from: message.role === 'user' ? 'me' : 'teri',
    text: message.body ?? '',
    at: formatTime(message.created_at),
    status: message.status,
    attachments: message.attachments ?? [],
    mentions: message.mentions ?? [],
    notes: [],
    visionBlind: false,
    previewUrls: {},
  };
}

/**
 * Coming back to a chat that is still being answered.
 *
 * The server writes the assistant row up front and only fills its body when
 * the stream ends, so a mid-answer fetch returns that row EMPTY with status
 * `streaming` — which is also what "the server died mid-answer" looks like.
 * Repaint it from the run we still hold instead, and drop it entirely when
 * nothing has arrived yet so the thinking indicator takes over.
 */
function reattachLiveRun(
  loaded: TeriMessage[],
  runs: Map<string, ActiveRun>,
  conversationId: string
): TeriMessage[] {
  const live = Array.from(runs.values()).find((r) => r.conversationId === conversationId);
  if (!live) return loaded;
  return loaded
    .filter((m) => !(m.id === live.assistantBubbleId && !live.streamed))
    .map((m) => {
      if (m.id === live.assistantBubbleId) {
        return { ...m, text: live.streamed, status: 'streaming' as const };
      }
      // The "couldn't use this" notes are client-side, so carry them back too.
      if (m.id === live.userBubbleId) {
        return { ...m, notes: live.notes, visionBlind: live.visionBlind };
      }
      return m;
    });
}

/**
 * Why a mention didn't make it. Deliberately about the data, not the person:
 * "their data" needs no assumption about anybody's pronouns.
 */
function droppedMentionNote(
  dropped: DroppedMention,
  requested: AssistantMention[]
): string {
  const label = requested.find((m) => m.id === dropped.id)?.label;
  const subject = label ?? (dropped.type === 'user' ? 'That person' : 'That item');
  return dropped.reason === 'forbidden'
    ? `${subject} wasn't included — you don't have permission to view their data.`
    : `${subject} wasn't included — I couldn't find it.`;
}

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeMembership } = useAuth();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dock, setDockState] = useState<AssistantDock>(() =>
    localStorage.getItem(DOCK_KEY) === 'sidebar' ? 'sidebar' : 'floating'
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState<string | null>(null);
  // Identifies the current *unsent* chat. Regenerated by `newChat` so a reply
  // still arriving for the previous blank chat can tell it has been left.
  const [draftKey, setDraftKey] = useState(() => localId('draft'));
  const [messages, setMessages] = useState<TeriMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [staged, setStaged] = useState<PendingAttachment[]>([]);
  const [pendingMentions, setPendingMentions] = useState<AssistantMention[]>([]);
  const [answeredNotice, setAnsweredNotice] = useState<AnsweredNotice | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const setDock = useCallback((next: AssistantDock) => {
    localStorage.setItem(DOCK_KEY, next);
    setDockState(next);
  }, []);

  // Publish the rail width once: index.css pads the body by it and the panel
  // sizes itself from it, so the squeeze and the panel can never disagree.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--teri-rail-w',
      `${ASSISTANT_RAIL_WIDTH}px`
    );
  }, []);

  // Squeeze the page for the rail. The flag lives on <html> so the rule in
  // index.css can gate it behind the desktop breakpoint — below `lg` the
  // panel is a sheet and must not steal width from a phone-sized viewport.
  useEffect(() => {
    const root = document.documentElement;
    if (!(open && dock === 'sidebar')) {
      delete root.dataset.teriDock;
      return undefined;
    }
    root.dataset.teriDock = 'sidebar';
    // Unmounting the provider must never leave the app permanently indented.
    return () => {
      delete root.dataset.teriDock;
    };
  }, [open, dock]);
  // pathname at SEND time, without making `send` identity churn on navigation.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  // ── In-flight replies ──────────────────────────────────────────────────
  // Keyed by run id, because more than one chat can be waiting at once: the
  // member asks in A, switches to B, and asks there too.
  const runsRef = useRef<Map<string, ActiveRun>>(new Map());
  const [busyKeys, setBusyKeys] = useState<string[]>([]);

  // Refs, not state: the stream callbacks below are long-lived closures and
  // need to know where the member is looking NOW, not at send time.
  const conversationIdRef = useRef<string | null>(conversationId);
  conversationIdRef.current = conversationId;
  const draftKeyRef = useRef(draftKey);
  draftKeyRef.current = draftKey;
  const openRef = useRef(open);
  openRef.current = open;

  const runKeyOf = (run: ActiveRun) => run.conversationId ?? `draft:${run.draftKey}`;
  const visibleKey = conversationId ?? `draft:${draftKey}`;
  const thinking = busyKeys.includes(visibleKey);
  const answeringIds = busyKeys.filter((key) => !key.startsWith('draft:'));

  /** Is this run's chat the one on screen? */
  const isRunVisible = useCallback((run: ActiveRun): boolean => {
    // Before `meta` a run has no conversation id, so an unsent chat is
    // identified by the draft key it was started in.
    return conversationIdRef.current !== null
      ? run.conversationId === conversationIdRef.current
      : run.draftKey === draftKeyRef.current;
  }, []);

  const syncBusyKeys = useCallback(() => {
    setBusyKeys(Array.from(runsRef.current.values(), runKeyOf));
  }, []);

  /** Immutably add-or-update one bubble by id. */
  const upsertMessage = useCallback(
    (id: string, patch: Partial<TeriMessage>, fallback: () => TeriMessage) => {
      setMessages((prev) => {
        const index = prev.findIndex((m) => m.id === id);
        if (index === -1) return [...prev, { ...fallback(), ...patch }];
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        return next;
      });
    },
    []
  );

  /** Stop is about the chat in front of the member, not every chat. */
  const stop = useCallback(() => {
    runsRef.current.forEach((run) => {
      if (isRunVisible(run)) run.controller.abort();
    });
  }, [isRunVisible]);

  const abortEverything = useCallback(() => {
    runsRef.current.forEach((run) => run.controller.abort());
    runsRef.current.clear();
    setBusyKeys([]);
  }, []);

  // Every object URL handed out this session, so none of them outlive the
  // messages that showed them (revoking twice is a harmless no-op).
  const previewUrlsRef = useRef<Set<string>>(new Set());

  const revokeAllPreviews = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
  }, []);

  const newChat = useCallback(() => {
    // Deliberately does NOT abort: a reply already on its way keeps streaming
    // into the chat that asked for it, and the member is told when it lands.
    setDraftKey(localId('draft'));
    setConversationId(null);
    setConversationTitle(null);
    setMessages([]);
    setDraft('');
    setPendingMentions([]);
    setHistoryOpen(false);
    setHasMore(false);
    setStaged([]);
    revokeAllPreviews();
    localStorage.removeItem(CONVERSATION_KEY);
  }, [revokeAllPreviews]);

  const openConversation = useCallback(
    async (id: string) => {
      setHistoryOpen(false);
      setLoadingConversation(true);
      try {
        const detail = await getConversation(id);
        setConversationId(id);
        setConversationTitle(detail.title);
        setMessages(reattachLiveRun(detail.messages.map(toTeriMessage), runsRef.current, id));
        setHasMore(detail.has_more);
        setPendingMentions([]);
        setDraft('');
        setStaged([]);
        revokeAllPreviews();
        // Arriving at the chat retires its own notice.
        setAnsweredNotice((prev) => (prev?.conversationId === id ? null : prev));
        localStorage.setItem(CONVERSATION_KEY, id);
      } catch {
        // A 404 here means the chat is gone (or never belonged to this
        // membership) — stop pointing at it so the next send starts fresh.
        localStorage.removeItem(CONVERSATION_KEY);
        toast.error("Couldn't open that chat. It may have been deleted.");
      } finally {
        setLoadingConversation(false);
      }
    },
    [revokeAllPreviews]
  );

  /** Prepend the previous page of turns. The list keeps scroll anchoring. */
  const loadOlder = useCallback(async () => {
    const oldest = messages.find((m) => !isLocalId(m.id));
    if (!conversationId || !hasMore || loadingOlder || !oldest) return;
    setLoadingOlder(true);
    try {
      const detail = await getConversation(conversationId, oldest.id);
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const older = detail.messages
          .filter((m) => !known.has(m.id))
          .map(toTeriMessage);
        return older.length ? [...older, ...prev] : prev;
      });
      setHasMore(detail.has_more);
    } catch {
      toast.error("Couldn't load the earlier part of this chat.");
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, hasMore, loadingOlder, messages]);

  // Resume the last chat. Deferred until the panel is first opened rather
  // than run on mount: a member who never touches Teri should not pay for a
  // conversation fetch on every page load.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (!open || resumedRef.current) return;
    resumedRef.current = true;
    const saved = localStorage.getItem(CONVERSATION_KEY);
    if (saved && !conversationId) void openConversation(saved);
  }, [open, conversationId, openConversation]);

  // A workspace switch changes which conversations exist — the current one
  // belongs to the old membership and must not follow the member across.
  const tenantId = activeMembership?.tenant?.id ?? null;
  const tenantRef = useRef(tenantId);
  useEffect(() => {
    const previous = tenantRef.current;
    if (previous === tenantId) return;
    tenantRef.current = tenantId;
    // `null → a workspace` is the session hydrating on load, not a switch.
    // Treating it as one would wipe the saved conversation id on every reload
    // and there would be nothing left to come back to.
    if (previous === null) return;
    resumedRef.current = true; // don't re-resume the previous workspace's chat
    // The one case where a running reply IS cut off: it belongs to a workspace
    // the member has left, and its answer can never be shown to them again.
    abortEverything();
    newChat();
  }, [abortEverything, tenantId, newChat]);

  const removeStaged = useCallback((key: string) => {
    setStaged((prev) => {
      const target = prev.find((item) => item.localKey === key);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      // The file was already stored server-side; let it go there too. It is
      // unbound until the message is sent, so this always succeeds — and a
      // failure is not something the member can act on.
      if (target?.attachment) void deleteAssistantAttachment(target.attachment.id).catch(() => {});
      return prev.filter((item) => item.localKey !== key);
    });
  }, []);

  const attachFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      const room = MAX_ATTACHMENTS_PER_TURN - staged.length;
      if (room <= 0) {
        toast.error(`Teri takes up to ${MAX_ATTACHMENTS_PER_TURN} files per message.`);
        return;
      }
      const accepted = files.slice(0, room);
      if (accepted.length < files.length) {
        toast.error(`Only ${room} more file${room === 1 ? '' : 's'} fit on this message.`);
      }

      await Promise.all(
        accepted.map(async (file) => {
          const key = localId('file');
          const previewUrl = file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : null;
          if (previewUrl) previewUrlsRef.current.add(previewUrl);
          setStaged((prev) => [...prev, { localKey: key, file, previewUrl, progress: 0 }]);
          try {
            const attachment = await uploadAssistantAttachment(file, {
              onUploadProgress: (event) => {
                const total = event.total ?? file.size;
                const pct = total ? Math.round((event.loaded / total) * 100) : 0;
                setStaged((prev) =>
                  prev.map((item) =>
                    item.localKey === key ? { ...item, progress: pct } : item
                  )
                );
              },
            });
            setStaged((prev) =>
              prev.map((item) =>
                item.localKey === key ? { ...item, progress: 100, attachment } : item
              )
            );
          } catch (err) {
            const message =
              (err as { response?: { data?: { message?: string } } })?.response?.data
                ?.message ?? 'Upload failed';
            setStaged((prev) =>
              prev.map((item) =>
                item.localKey === key ? { ...item, error: message } : item
              )
            );
            toast.error(message);
          }
        })
      );
    },
    [staged.length]
  );

  const addMention = useCallback((mention: AssistantMention) => {
    setPendingMentions((prev) => {
      if (prev.some((m) => m.id === mention.id)) return prev;
      if (prev.length >= 5) {
        toast.error('Teri looks at up to 5 mentions per message.');
        return prev;
      }
      return [...prev, mention];
    });
  }, []);

  const removeMention = useCallback((id: string) => {
    setPendingMentions((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // Revoke outstanding previews, and cut every stream, if the provider goes
  // away — at that point there is nobody left to paint the answer for.
  useEffect(
    () => () => {
      revokeAllPreviews();
      abortEverything();
    },
    [abortEverything, revokeAllPreviews]
  );

  /**
   * Tell the member an answer landed somewhere they aren't looking. Only the
   * latest is kept: two notices stacked up is a queue to clear, not news.
   */
  const notifyAnswered = useCallback((run: ActiveRun, status: AssistantMessageStatus) => {
    if (!run.conversationId) return;
    setAnsweredNotice({
      conversationId: run.conversationId,
      title: run.title?.trim() || 'your other chat',
      status,
    });
  }, []);

  const dismissAnsweredNotice = useCallback(() => setAnsweredNotice(null), []);

  const openAnsweredNotice = useCallback(() => {
    const notice = answeredNotice;
    setAnsweredNotice(null);
    if (!notice) return;
    setOpen(true);
    if (notice.conversationId !== conversationIdRef.current) {
      void openConversation(notice.conversationId);
    }
  }, [answeredNotice, openConversation]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (trimmed === '' || thinking) return;
      // Uploads still in flight: their ids don't exist yet, so sending now
      // would silently drop the files the member deliberately attached.
      if (staged.some((item) => !item.attachment && !item.error)) return;

      setDraft('');
      const sent = staged.filter((item) => !!item.attachment);
      const settled = sent.map((item) => item.attachment as AssistantAttachment);
      // The preview URL moves from the composer into the bubble instead of
      // being revoked: the member should keep seeing the image they attached.
      const previewUrls: Record<string, string> = {};
      sent.forEach((item) => {
        if (item.previewUrl && item.attachment) {
          previewUrls[item.attachment.id] = item.previewUrl;
        }
      });
      const mentions = pendingMentions;
      const stamp = timeNow();
      // `const`, deliberately: React invokes state updaters LAZILY, so an
      // updater that read a reassignable id would see whatever `meta` had
      // already changed it to — the optimistic bubble would be appended under
      // its final id and the patch below would then match nothing. The live
      // ids live on `run`; these two never change.
      const optimisticUserId = localId('user');
      const optimisticAssistantId = localId('assistant');

      setMessages((prev) => [
        ...prev,
        {
          id: optimisticUserId,
          from: 'me',
          text: trimmed,
          at: stamp,
          status: 'complete',
          attachments: settled,
          mentions,
          notes: [],
          visionBlind: false,
          previewUrls,
        },
      ]);
      // Only the previews we did NOT hand to the bubble are revoked here.
      setStaged((prev) => {
        prev.forEach((item) => {
          const kept = item.attachment && previewUrls[item.attachment.id];
          if (item.previewUrl && !kept) URL.revokeObjectURL(item.previewUrl);
        });
        return [];
      });
      setPendingMentions([]);

      let failed = false;
      const controller = new AbortController();
      const run: ActiveRun = {
        id: localId('run'),
        conversationId,
        draftKey,
        controller,
        userBubbleId: optimisticUserId,
        assistantBubbleId: optimisticAssistantId,
        stamp,
        streamed: '',
        title: conversationTitle,
        notes: [],
        visionBlind: false,
      };
      runsRef.current.set(run.id, run);
      syncBusyKeys();

      /** Takes its id as an argument for the same lazy-updater reason. */
      const newAssistantBubble = (id: string): TeriMessage => ({
        id,
        from: 'teri',
        text: '',
        at: stamp,
        status: 'streaming',
        attachments: [],
        mentions: [],
        notes: [],
        visionBlind: false,
        previewUrls: {},
      });

      const onMeta = (meta: ChatMeta) => {
        const notes = meta.dropped_mentions.map((d) => droppedMentionNote(d, mentions));
        if (meta.dropped_attachments.length > 0) {
          notes.push(
            `${meta.dropped_attachments.join(', ')} didn't fit in this question, so I read the rest without it.`
          );
        }
        const visionBlind = settled.some((a) => a.kind === 'image') && !meta.vision;
        // Read visibility BEFORE the run learns its conversation id, or a
        // brand-new chat would compare 'C1' against a still-null view.
        const visible = isRunVisible(run);

        run.conversationId = meta.conversation_id;
        run.title = meta.title;
        run.userBubbleId = meta.user_message_id;
        run.assistantBubbleId = meta.assistant_message_id;
        run.notes = notes;
        run.visionBlind = visionBlind;
        syncBusyKeys();

        if (visible) {
          setConversationId(meta.conversation_id);
          setConversationTitle(meta.title);
          localStorage.setItem(CONVERSATION_KEY, meta.conversation_id);
          // Swap the optimistic id for the real one so a reload lines up, and
          // hang the "here's what I couldn't use" notes off the question.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === optimisticUserId
                ? { ...m, id: meta.user_message_id, notes, visionBlind }
                : m
            )
          );
        }
        // The chat now exists (or just moved to the top) in the history list.
        queryClient.invalidateQueries({ queryKey: assistantKeys.list });
      };

      try {
        await streamAssistantChat(
          {
            conversationId,
            message: trimmed,
            attachments: settled.map((a) => a.id),
            mentions: mentions.map(({ type, id }) => ({ type, id })),
            page: pageFor(pathRef.current),
          },
          {
            onMeta,
            onDelta: (delta) => {
              run.streamed += delta;
              if (!run.streamed) return;
              // Off-screen runs keep draining the stream but stop painting —
              // writing into `messages` now would put this answer in whatever
              // chat the member has since opened.
              if (!isRunVisible(run)) return;
              const bubbleId = run.assistantBubbleId;
              upsertMessage(bubbleId, { text: run.streamed, status: 'streaming' }, () =>
                newAssistantBubble(bubbleId)
              );
            },
            onError: () => {
              failed = true;
            },
          },
          controller.signal
        );
        if (!run.streamed) throw new Error('empty');
        settle(run, failed ? 'failed' : 'complete', run.streamed);
      } catch (err) {
        if (controller.signal.aborted) {
          // Stopped on purpose. Keep the half-written answer — it is real
          // content Teri produced, and the server persisted it as `aborted`
          // — but leave no bubble at all if nothing had arrived yet, rather
          // than blaming the network for a choice.
          if (run.streamed) settle(run, 'aborted', run.streamed, { notify: false });
        } else {
          const friendly =
            err instanceof AssistantRequestError && err.friendly ? err.message : null;
          settle(
            run,
            'failed',
            friendly ??
              "Sorry — I couldn't reach my brain just now. Please try again in a moment."
          );
        }
      } finally {
        runsRef.current.delete(run.id);
        syncBusyKeys();
        queryClient.invalidateQueries({ queryKey: assistantKeys.list });
      }

      /** Paint the finished answer, or say where it landed if it is off-screen. */
      function settle(
        finished: ActiveRun,
        status: AssistantMessageStatus,
        text: string,
        options: { notify?: boolean } = {}
      ) {
        if (isRunVisible(finished)) {
          const bubbleId = finished.assistantBubbleId;
          upsertMessage(bubbleId, { text, status }, () => newAssistantBubble(bubbleId));
          // Painted, but into a panel nobody can see.
          if (!openRef.current && options.notify !== false) notifyAnswered(finished, status);
          return;
        }
        if (options.notify !== false) notifyAnswered(finished, status);
      }
    },
    [
      conversationId,
      conversationTitle,
      draftKey,
      isRunVisible,
      notifyAnswered,
      pendingMentions,
      queryClient,
      staged,
      syncBusyKeys,
      thinking,
      upsertMessage,
    ]
  );

  return (
    <AssistantContext.Provider
      value={{
        open,
        setOpen,
        dock,
        setDock,
        conversationId,
        conversationTitle,
        messages,
        draft,
        setDraft,
        thinking,
        answeringIds,
        answeredNotice,
        openAnsweredNotice,
        dismissAnsweredNotice,
        send,
        stop,
        newChat,
        openConversation,
        loadingConversation,
        hasMore,
        loadingOlder,
        loadOlder,
        historyOpen,
        setHistoryOpen,
        staged,
        attachFiles,
        removeStaged,
        pendingMentions,
        addMention,
        removeMention,
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
};

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within an AssistantProvider');
  return ctx;
}

/** The greeting is client-side chrome, never a message: it must not be
 *  persisted into a transcript or replayed to the provider. */
export function useTeriGreeting(): string {
  const { user } = useAuth();
  return `Hey ${user?.first_name || 'there'}, as always, Teri at your service!`;
}
